/**
 * Dashed-border CTA shown on HomeScreen when a guest scans a QR but hasn't
 * started/joined a group yet. Tapping opens GroupStartSheet; the (×) chip
 * dismisses for the rest of the session.
 *
 * Hidden when there's no table context (online-only flow doesn't get this).
 */
import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { GroupIllo } from "./GroupIllo";
import { useTable } from "@/store/table";
import { scopedStorageKey } from "@/lib/storageScope";

const DISMISS_KEY = "tappya_group_offer_dismissed_v2";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(scopedStorageKey(DISMISS_KEY)) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    sessionStorage.setItem(scopedStorageKey(DISMISS_KEY), "1");
  } catch {
    /* storage unavailable */
  }
}

export interface GroupOfferBannerProps {
  onTap: () => void;
}

export function GroupOfferBanner({ onTap }: GroupOfferBannerProps) {
  const { t } = useTranslation();
  const { tableInfo } = useTable();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!tableInfo || dismissed) return null;

  const handleDismiss = (e: MouseEvent) => {
    e.stopPropagation();
    writeDismissed();
    setDismissed(true);
  };

  return (
    <button
      data-testid="group-offer"
      onClick={onTap}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "calc(100% - 32px)",
        margin: "12px 16px 0",
        padding: "12px 14px",
        background: "var(--surface-2)",
        border: "1.5px dashed var(--border-strong, #cbd5e1)",
        borderRadius: 14,
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
        color: "var(--ink-1)",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <GroupIllo size={56} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {t("v2.group_cart.offer_title")}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>
          {t("v2.group_cart.offer_sub")}
        </div>
      </div>
      <span
        role="button"
        tabIndex={0}
        aria-label={t("v2.common.close", "Dismiss")}
        onClick={handleDismiss}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleDismiss(e as unknown as MouseEvent);
          }
        }}
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: "var(--surface-1)",
          display: "grid",
          placeItems: "center",
          color: "var(--ink-3)",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        ×
      </span>
    </button>
  );
}
